<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import DatePicker from '../../Shared/DatePicker.vue'
import SsnInput from '../../Shared/SSNInput.vue'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

const rules = {
  umsokn: {
    sjodsfelagiName: { required },
    sjodsfelagiSsn: { required },
    sjodsFelagiDeath: { required },
    elliOrOrorku: { required },
  }
}

const deathDateMenu = ref(false)

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  }
})

function todayISO() {
  return new Date().toISOString().split('T')[0]
}
</script>

<template>
  <div v-if="umsokn">
    <v-row>
      <v-col>
        <h2 class="form__header">
          {{ t('barnalifeyrir_umsokn_step2_header') }}
        </h2>
      </v-col>
    </v-row>

    <!-- Name + SSN -->
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.sjodsfelagiName"
          :label="t('barnalifeyrir_umsokn_step2_nafn')"
          :readonly="props.disable"
          variant="outlined"
          density="comfortable"
          :error="v$.umsokn.sjodsfelagiName.$error"
          :error-messages="v$.umsokn.sjodsfelagiName.$error ? [t('barnalifeyrir_umsokn_step2_nafn_error')] : []"
        />
      </v-col>
      <v-col cols="12" md="6">
        <SsnInput
          v-model="umsokn.sjodsfelagiSsn"
          :label="t('barnalifeyrir_umsokn_step2_ssn')"
          :readonly="props.disable"
          variant="outlined"
          density="comfortable"
          :error="v$.umsokn.sjodsfelagiSsn.$error"
          :error-messages="v$.umsokn.sjodsfelagiSsn.$error ? [t('barnalifeyrir_umsokn_step2_ssn_error')] : []"
        />
      </v-col>
    </v-row>

    <!-- Death date -->
  <v-row>
    <v-col cols="12" md="6">
      <DatePicker
        v-model="umsokn.sjodsFelagiDeath"
        :label="t('barnalifeyrir_umsokn_step2_danardagur')"
        :disabled="props.disable"
        :max="todayISO()"
        :error="v$.umsokn?.sjodsFelagiDeath?.$error"
        :error-messages="v$.umsokn.sjodsFelagiDeath.$error ? [t('barnalifeyrir_umsokn_step2_danardagur_error')] : []"
      />
    </v-col>
  </v-row>

    <!-- Pension fund member? -->
    <v-row>
      <v-col>
        <p>{{ t('barnalifeyrir_umsokn_step2_sjodfelagi') }}</p>
        <v-radio-group
          v-model="umsokn.elliOrOrorku"
          :readonly="props.disable"
          inline
          hide-details
          :error="v$.umsokn.elliOrOrorku.$error"
          :error-messages="v$.umsokn.elliOrOrorku.$error ? [t('barnalifeyrir_umsokn_step2_sjodfelagi_error')] : []"
        >
          <v-radio :label="t('ororku_umsokn_step3_yes')" :value="1" />
          <v-radio :label="t('ororku_umsokn_step3_no')" :value="2" />
        </v-radio-group>
      </v-col>
    </v-row>

    <!-- Note -->
    <v-row>
      <v-col>
        <p style="font-weight:bold">
          {{ t('barnalifeyrir_umsokn_step2_athygli') }}
        </p>
      </v-col>
    </v-row>
  </div>
</template>
